/**
 * O VOCABULÁRIO DA NORMA — as palavras exatas que ligam uma amostra à tabela de
 * limites (NBR 10576).
 *
 * POR QUE ISTO EXISTE. O motor casa a faixa por COMPARAÇÃO EXATA de texto: a
 * classe de tensão do equipamento tem de ser, letra por letra, a mesma que está
 * na faixa cadastrada. É de propósito — "13,8 kV" não é "≤ 36,2 kV", e escolher
 * a faixa "mais parecida" seria inventar limite em laudo assinado. O efeito
 * colateral é que UM caractere diferente faz o diagnóstico sair em branco sem
 * explicação, e foi exatamente isso que aconteceu com o Físico-Químico.
 *
 * Enquanto estas listas viviam copiadas em cada tela (e em nenhum lugar do
 * servidor), cada porta de entrada podia gravar um texto diferente: o cadastro
 * de equipamentos, a aba Identificação e a IA que transcreve a norma. Agora é
 * uma fonte só, que o front e o servidor usam — o servidor importa de `src/`
 * como já faz com o questionário de clima e o formulário de avaliação.
 *
 * ⚠️ Mudar um texto daqui NÃO renomeia o que já está gravado no banco: as
 * faixas cadastradas continuam com o texto antigo e deixariam de casar. Se um
 * dia precisar mudar, mude junto em `limites_normativos` (e na ficha dos
 * equipamentos), com o sistema parado.
 */

/**
 * As quatro classes de tensão da NBR 10576 (o texto está no cadastro de faixas).
 * O símbolo é "≤" (menor ou igual), não "<=".
 */
export const CLASSES_TENSAO_NORMA = [
  "≤ 36,2 kV",
  "> 36,2 kV e ≤ 72,5 kV",
  "> 72,5 kV e ≤ 145 kV",
  "> 145 kV",
] as const;

/**
 * A condição do óleo NA COLETA — é dela que sai a tabela da norma que vale.
 * Muda a cada amostra (o mesmo transformador é "em serviço" numa coleta e
 * "após regeneração" na seguinte), por isso fica na análise e não no equipamento.
 */
export const CONDICOES_OLEO_NORMA = [
  "Em serviço (preventiva)",
  "Novo ou após troca de óleo",
  "Após recondicionamento (termovácuo)",
  "Após regeneração",
] as const;

/** Tipos de óleo isolante que o cadastro de equipamentos oferece. */
export const TIPOS_OLEO_NORMA = ["Mineral", "Vegetal", "Silicone"] as const;

/**
 * Como o comutador de derivações em carga (CDC) se liga ao tanque principal —
 * o eixo `arranjo` da NBR 7274. O comutador comunicante gera acetileno em
 * operação normal, e por isso o valor típico de C₂H₂ muda (2 contra 60 ppm).
 *
 * São TODOS os arranjos possíveis, e isso é usado: um acetileno abaixo do
 * típico dos dois arranjos está abaixo qualquer que seja o do equipamento
 * (server/valores-tipicos-gases.ts). Acrescentar um arranjo aqui sem cadastrar
 * o valor típico dele desliga esse atalho, como deve ser.
 */
export const ARRANJOS_CDC_NORMA = [
  "Sem CDC (ou CDC não comunicante)",
  "CDC com comunicação",
] as const;

/**
 * Compara dois textos do jeito que uma PESSOA compararia: sem acento, sem
 * diferença de caixa, sem espaço sobrando e aceitando "<=" no lugar de "≤".
 *
 * Serve só para RECONHECER o que a pessoa (ou a IA) quis dizer e devolver o
 * texto canônico — a comparação que decide a faixa continua exata, sobre o
 * texto já canonizado.
 */
function normalizar(v: string): string {
  return String(v ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/<=/g, "≤")
    .replace(/>=/g, "≥")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * Devolve o texto CANÔNICO da lista quando reconhece o valor; `null` quando o
 * valor está vazio (faixa genérica, que é válida); e `undefined` quando o texto
 * não corresponde a nada — caso em que quem chama decide se recusa ou avisa.
 *
 * Os três desfechos são diferentes de propósito: "não informado" e "informado
 * errado" pedem consertos diferentes, e tratá-los igual foi o que produziu a
 * mensagem "falta limite cadastrado" quando o que faltava era outra coisa.
 */
export function canonizar(
  valor: unknown,
  opcoes: readonly string[],
): string | null | undefined {
  const bruto = String(valor ?? "").trim();
  if (!bruto) return null;
  const alvo = normalizar(bruto);
  return opcoes.find((o) => normalizar(o) === alvo);
}

/** O texto está no vocabulário da norma? (usado pelos avisos das telas) */
export function noVocabulario(valor: unknown, opcoes: readonly string[]): boolean {
  const r = canonizar(valor, opcoes);
  return r === null || typeof r === "string";
}
